import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, X, ArrowLeftRight, Gamepad2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComparePage = () => {
  const { theme } = useTheme();
  const [comparisons, setComparisons] = useState([]);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchComparisons = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/compare`);
        setComparisons(response.data);
      } catch (error) {
        console.error('Error fetching comparisons:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchComparisons();
  }, []);

  const fetchComparisonDetails = async (comparisonId) => {
    try {
      const response = await axios.get(`${API_URL}/api/compare/${comparisonId}`);
      setComparisonData(response.data);
      setSelectedComparison(comparisonId);
    } catch (error) {
      console.error('Error fetching comparison details:', error);
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-[#F5F5F7]'}`}>
      <header className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} border-b`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-[#52525B] dark:text-gray-400 hover:text-[#0A0A0A] dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#8B5CF6]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ArrowLeftRight className="w-8 h-8 text-[#8B5CF6]" />
            </div>
            <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
              Comparador de Modelos
            </h1>
            <p className="text-[#52525B] dark:text-gray-400">
              Compara consolas y elige la mejor opción para ti
            </p>
          </div>

          {!selectedComparison ? (
            // List of comparisons
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full text-center py-12">
                  <div className="w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : (
                comparisons.map((comparison, index) => (
                  <motion.button
                    key={comparison.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => fetchComparisonDetails(comparison.id)}
                    className={`${
                      theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:border-[#8B5CF6]' : 'bg-white border-[#E5E7EB] hover:border-[#8B5CF6]'
                    } border rounded-xl p-6 text-left transition-all hover:-translate-y-1 hover:shadow-lg`}
                  >
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#0047FF]/10 rounded-full flex items-center justify-center">
                        <Gamepad2 className="w-6 h-6 text-[#0047FF]" />
                      </div>
                      <ArrowLeftRight className="w-5 h-5 text-[#A1A1AA]" />
                      <div className="w-12 h-12 bg-[#8B5CF6]/10 rounded-full flex items-center justify-center">
                        <Gamepad2 className="w-6 h-6 text-[#8B5CF6]" />
                      </div>
                    </div>
                    <p className={`text-center font-semibold ${theme === 'dark' ? 'text-white' : ''}`}>
                      {comparison.model_a}
                    </p>
                    <p className="text-center text-[#A1A1AA] text-sm my-1">vs</p>
                    <p className={`text-center font-semibold ${theme === 'dark' ? 'text-white' : ''}`}>
                      {comparison.model_b}
                    </p>
                  </motion.button>
                ))
              )}
            </div>
          ) : comparisonData && (
            // Comparison details
            <div>
              <button
                onClick={() => {
                  setSelectedComparison(null);
                  setComparisonData(null);
                }}
                className="mb-6 flex items-center gap-2 text-[#0047FF] hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Ver todas las comparaciones
              </button>

              <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-[#E5E7EB]'} rounded-xl border overflow-hidden`}>
                {/* Header row */}
                <div className="grid grid-cols-3 border-b border-[#E5E7EB] dark:border-gray-700">
                  <div className="p-4" />
                  <div className="p-4 text-center bg-[#0047FF]/5">
                    <p className={`font-bold text-lg ${theme === 'dark' ? 'text-white' : ''}`}>
                      {comparisonData.model_a.name}
                    </p>
                    <p className="text-sm text-[#52525B] dark:text-gray-400">
                      {comparisonData.model_a.release_year}
                    </p>
                    <p className="text-lg font-bold text-[#0047FF] mt-2">
                      {comparisonData.model_a.price_launch}€
                    </p>
                  </div>
                  <div className="p-4 text-center bg-[#8B5CF6]/5">
                    <p className={`font-bold text-lg ${theme === 'dark' ? 'text-white' : ''}`}>
                      {comparisonData.model_b.name}
                    </p>
                    <p className="text-sm text-[#52525B] dark:text-gray-400">
                      {comparisonData.model_b.release_year}
                    </p>
                    <p className="text-lg font-bold text-[#8B5CF6] mt-2">
                      {comparisonData.model_b.price_launch}€
                    </p>
                  </div>
                </div>

                {/* Specs rows */}
                {[
                  { label: 'Dimensiones', key: 'dimensions' },
                  { label: 'Peso', key: 'weight' },
                  { label: 'Almacenamiento', key: 'storage' }
                ].map((spec) => (
                  <div key={spec.key} className="grid grid-cols-3 border-b border-[#E5E7EB] dark:border-gray-700">
                    <div className={`p-4 font-medium ${theme === 'dark' ? 'text-white' : ''}`}>
                      {spec.label}
                    </div>
                    <div className="p-4 text-center text-[#52525B] dark:text-gray-400">
                      {comparisonData.model_a[spec.key]}
                    </div>
                    <div className="p-4 text-center text-[#52525B] dark:text-gray-400">
                      {comparisonData.model_b[spec.key]}
                    </div>
                  </div>
                ))}

                {/* Features */}
                <div className="grid grid-cols-3 border-b border-[#E5E7EB] dark:border-gray-700">
                  <div className={`p-4 font-medium ${theme === 'dark' ? 'text-white' : ''}`}>
                    Características
                  </div>
                  <div className="p-4">
                    <ul className="space-y-1">
                      {comparisonData.model_a.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#52525B] dark:text-gray-400">
                          <Check className="w-4 h-4 text-[#10B981]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4">
                    <ul className="space-y-1">
                      {comparisonData.model_b.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#52525B] dark:text-gray-400">
                          <Check className="w-4 h-4 text-[#10B981]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Pros */}
                <div className="grid grid-cols-3 border-b border-[#E5E7EB] dark:border-gray-700">
                  <div className={`p-4 font-medium text-[#10B981]`}>
                    Ventajas
                  </div>
                  <div className="p-4">
                    <ul className="space-y-1">
                      {comparisonData.model_a.pros.map((pro, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#10B981]">
                          <Check className="w-4 h-4" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4">
                    <ul className="space-y-1">
                      {comparisonData.model_b.pros.map((pro, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#10B981]">
                          <Check className="w-4 h-4" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Cons */}
                <div className="grid grid-cols-3 border-b border-[#E5E7EB] dark:border-gray-700">
                  <div className={`p-4 font-medium text-[#EF4444]`}>
                    Desventajas
                  </div>
                  <div className="p-4">
                    <ul className="space-y-1">
                      {comparisonData.model_a.cons.map((con, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#EF4444]">
                          <X className="w-4 h-4" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4">
                    <ul className="space-y-1">
                      {comparisonData.model_b.cons.map((con, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#EF4444]">
                          <X className="w-4 h-4" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="p-6 bg-[#0047FF]/5">
                  <p className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : ''}`}>
                    Nuestra recomendación
                  </p>
                  <p className="text-[#52525B] dark:text-gray-400">
                    {comparisonData.recommendation}
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ComparePage;
